import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  FormControl,
  Box,
  Button,
  TextField,
  Stack,
  Fab,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import API from 'services/AxiosConfig';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';

interface editApplicantsProgressProps {
  closeMe: () => void;
  currentRow?: any;
  id: any;
  title?: any;
  initialVals?: any;
  fetchData: () => void;
  idx?: number;
  URLPATH?: any;
  open: boolean;
  type: any;
}

const ApplicantProgressModalContent = ({
  closeMe,
  currentRow,
  id,
  title,
  initialVals = { row1: '' },
  idx,
  URLPATH,
  type,
}: editApplicantsProgressProps) => {
  const [rows, setRows] = useState<any[]>([]);
  const [current, setCurrent] = useState<any>([]);
  const [isEditable, setIsEditable] = useState(true);

  const [isLoading, setIsLoading] = useState(false);
  const [updatedValidationSchema, setUpdatedValidationSchema] = useState({
    title: yup.string().required('Title is Required'),
  });

  const editApplicantProgress = async (values: any) => {
    setIsLoading(true);
    API.put(`${URLPATH}/labels/${id}`, { values, oldTitle: title, idx })
      .then((rsp) => {
        if (rsp.data.success) {
          setIsLoading(false);
          toast.success(`Successfully Edited ${type === 0 ? 'Applicant' : 'Prospect'} Progress Card.`);
          closeMe();
        }
      })
      .catch((error) => {
        setIsLoading(false);
        toast.error(`Failed to Edit ${type === 0 ? 'Applicant' : 'Prospect'} Progress Card.`);
        console.error(error);
      });
  };

  const createApplicantProgress = (values: any) => {
    let title = values.title.trim();
    const doc: any = { title, values: {} };
    delete values.title;

    Object.values(values).forEach((item: any) => {
      if (item !== false) {
        doc.values[item.trim()] = '';
      }
    });
    setIsLoading(true);
    API.post(`${URLPATH}/${id}`, doc)
      .then((rsp) => {
        if (rsp.data.success) {
          setIsLoading(false);
          toast.success(`Successfully Added ${type === 0 ? 'Applicant' : 'Prospect'} Progress Card`);
          closeMe();
        }
      })
      .catch((error) => {
        setIsLoading(false);
        toast.error('Failed to add applicant.');
        console.error(error);
      });
  };

  const removeApplicant = () => {
    setIsLoading(true);
    API.delete(`${URLPATH}/${title}`)
      .then((rsp) => {
        if (rsp.data.success) {
          setIsLoading(false);
          toast.success(`Successfully Removed ${type === 0 ? 'Applicant' : 'Prospect'} Progress Card.`);
          closeMe();
        }
      })
      .catch((error) => {
        setIsLoading(false);
        if (error.response?.data?.error === 'Cannot delete stage with active applicants') {
          toast.error('Cannot delete stage with active applicants');
        } else {
          toast.error('Failed To Remove Applicant Progress Card.');
        }
      });
  };

  const formik = useFormik({
    initialValues: {
      title: title || '',
      ...initialVals,
    },
    enableReinitialize: true,
    validationSchema: yup.object(updatedValidationSchema),
    onSubmit: (values) => {
      const filtered = Object.entries(values).filter(([key, value]) => key !== 'title' && value !== false);

      if (filtered.length === 0) {
        toast.error('Must contain at least one row');
        return;
      }

      currentRow ? editApplicantProgress(values) : createApplicantProgress(values);
    },
  });

  useEffect(() => {
    setCurrent(currentRow);
    setIsEditable(!(title?.toLowerCase() === 'closed lost' || title?.toLowerCase() === 'closed won'));
  }, []);

  useEffect(() => {
    const updated: any = {};
    for (const key in formik.values) {
      if (key !== 'title' && !(currentRow && key === 'row1')) {
        updated[key] = yup.string().required('Row name required');
      }
    }
    setUpdatedValidationSchema((prev) => ({ ...prev, ...updated }));
  }, []);

  return (
    <Dialog maxWidth="sm" fullWidth open={true} onClose={closeMe}>
      <Box display="flex" justifyContent="space-between" alignItems="center" px={3} pt={2}>
        <DialogTitle sx={{ p: 0 }}>{idx ? 'Edit Stage' : 'Add Stage'}</DialogTitle>
        <IconButton onClick={closeMe}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={10} sx={{ paddingLeft: '7px!important' }}>
                <TextField
                  label="Title"
                  name="title"
                  fullWidth
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  disabled={!isEditable}
                />
              </Grid>
              <Grid item xs={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    const newIndex = rows.length + 2;
                    setRows([...rows, {}]);
                    formik.setFieldValue(`row${newIndex}`, '');
                  }}>
                  Add Row
                </Button>
              </Grid>
            </Grid>

            {!currentRow && (
              <Grid container spacing={1} alignItems="center"><Grid item xs={11} ><TextField
                fullWidth
                label="Row 1"
                name="row1"
                value={formik.values.row1 || ''}
                onChange={formik.handleChange}
                error={formik.touched.row1 && Boolean(formik.errors.row1)}
                helperText={formik.touched.row1 && formik.errors.row1}
              /></Grid><Grid item xs={1}><Tooltip title="Default Row">
                <IconButton
                  color="error"
                  disabled={true}
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip></Grid></Grid>
            )}

            {current?.map((field: any, i: number) => (
              <Grid container spacing={1} alignItems="center" key={i}>
                <Grid item xs={11}>
                  <TextField
                    fullWidth
                    name={field.label}
                    value={formik.values[field.label] || ''}
                    onChange={formik.handleChange}
                    error={formik.touched[field.label] && Boolean(formik.errors[field.label])}
                    helperText={formik.touched[field.label] && formik.errors[field.label]}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Tooltip title="Remove Row">
                    <IconButton
                      color="error"
                      onClick={(e) => {
                        e.preventDefault();
                        const updated = [...current];
                        formik.setFieldValue(updated[i].label, false);
                        updated.splice(i, 1);
                        setCurrent(updated);
                      }}>
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            ))}

            {rows.map((_, i) => (
              <Grid container spacing={1} alignItems="center" key={i}>
                <Grid item xs={11}>
                  <TextField
                    fullWidth
                    label={`Row ${(current ? current.length : 0) + i + 1}`}
                    name={`row${i + 2}`}
                    value={formik.values[`row${i + 2}`] || ''}
                    onChange={formik.handleChange}
                    error={formik.touched[`row${i + 2}`] && Boolean(formik.errors[`row${i + 2}`])}
                    helperText={formik.touched[`row${i + 2}`] && formik.errors[`row${i + 2}`]}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Tooltip title="Remove Row">
                    <IconButton
                      color="error"
                      onClick={(e) => {
                        e.preventDefault();
                        const updatedRows = [...rows];
                        updatedRows.splice(i, 1);
                        setRows(updatedRows);
                        formik.setFieldValue(`row${i + 2}`, false);
                      }}>
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            ))}
          </Stack>
          {isLoading ?
            <>
              <Grid container paddingTop={1} justifyContent="center">
                <CircularProgress size={'28px'} style={{ marginRight: '50px' }} />
              </Grid>
            </> :
            <Box mt={3} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
              {currentRow && isEditable && (
                <Tooltip title="Delete Progress Card">
                  <Button variant="contained" color="error" onClick={removeApplicant}>
                    Remove
                  </Button>
                </Tooltip>
              )}
              <Button variant="outlined" color="secondary" onClick={closeMe}>
                Close
              </Button>
              <Button variant="contained" color="primary" type="submit">
                Submit
              </Button>
            </Box>}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ApplicantProgressModalContent;
